import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, FileDown, FileSpreadsheet, Loader2, ShieldCheck, LogOut } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import FiltersPanel from "@/components/FiltersPanel";
import KpiCards from "@/components/KpiCards";
import EncuestaCard from "@/components/EncuestaCard";
import ClassificationChart from "@/components/ClassificationChart";
import MapView from "@/components/MapView";
import SeccionInfoCards from "@/components/SeccionInfoCards";
import { useAuth } from "@/context/AuthContext";
import {
  fetchFilters, fetchPoints, fetchSummary,
  fetchMeta, triggerSync, reportUrl, exportUrl, fetchGeojson, logoutApi,
  fetchAreasGeojson, fetchColoniasGeojson, fetchMapConfig, resolveSearch, fetchCseccion,
  LEVEL_OF, LEVEL_SINGULAR, LEVEL_ARTICLE,
} from "@/lib/api";

const EMPTY = { unidad: "", categoria: "" };

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const onLogout = async () => {
    try { await logoutApi(); } catch (e) { /* ignore */ }
    setUser(null);
    navigate("/login", { replace: true });
  };
  const [filters, setFilters] = useState(null);
  const [values, setValues] = useState(EMPTY);
  const [layers, setLayers] = useState({ actividades: true, rac: true, activaciones: true });
  const [points, setPoints] = useState([]);
  const [geojson, setGeojson] = useState(null);
  const [areasGeojson, setAreasGeojson] = useState(null);
  const [coloniasGeojson, setColoniasGeojson] = useState(null);
  const [cseccion, setCseccion] = useState([]);
  const [mapConfig, setMapConfig] = useState({ secciones: true, areas: true, colonias: true });
  const [activeLayer, setActiveLayer] = useState(null);      // "secciones" | "areas" | "colonias"
  const [selectedValue, setSelectedValue] = useState(null);  // clicked polygon value
  const [selectedLevel, setSelectedLevel] = useState(null);  // level of the active selection
  const [searchText, setSearchText] = useState("");          // search input text
  const [appliedSearch, setAppliedSearch] = useState("");    // committed search (Enter / button)
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const debounceRef = useRef(null);

  const activeLevel = activeLayer ? LEVEL_OF[activeLayer] : "seccion";
  const hasSelection = selectedValue != null && selectedValue !== "";
  const effectiveLevel = hasSelection ? (selectedLevel || activeLevel) : activeLevel;
  const levelLabel = LEVEL_SINGULAR[effectiveLevel] || "Sección";

  const setValue = (k, v) => setValues((p) => ({ ...p, [k]: v }));
  const toggleLayer = (k) => setLayers((p) => ({ ...p, [k]: !p[k] }));
  const onReset = () => {
    setValues(EMPTY);
    setSelectedValue(null);
    setSelectedLevel(null);
    setSearchText("");
    setAppliedSearch("");
  };
  const onSearchSubmit = useCallback(async () => {
    const q = searchText.trim();
    if (!q) { setAppliedSearch(""); setSelectedValue(null); setSelectedLevel(null); return; }
    try {
      const res = await resolveSearch(q);
      const layerKey = res.layer;
      if (res.match && res.match !== "point" && res.match !== "none" && layerKey) {
        const lbl = LEVEL_SINGULAR[LEVEL_OF[layerKey]] || "";
        if (!mapConfig[layerKey]) {
          toast.info(`La capa ${LEVEL_SINGULAR[LEVEL_OF[layerKey]]}s está deshabilitada; mostrando resultados por texto`);
        } else {
          setAppliedSearch("");
          setActiveLayer(layerKey);
          setSelectedValue(res.value);
          setSelectedLevel(LEVEL_OF[layerKey]);
          toast.success(`Coincidencia: ${lbl} ${res.value}`);
          return;
        }
      }
    } catch (e) { /* fall back to text search */ }
    // Folio / dirección (o sin coincidencia de capa): búsqueda de texto, se mantiene la capa actual.
    setSelectedValue(null);
    setSelectedLevel(null);
    setAppliedSearch(q);
  }, [searchText, mapConfig]);

  const onPickSuggestion = useCallback((item) => {
    if (!item?.layer) return;
    setSearchText(item.value);
    if (!mapConfig[item.layer]) {
      toast.info(`La capa ${LEVEL_SINGULAR[LEVEL_OF[item.layer]]}s está deshabilitada; mostrando resultados por texto`);
      setSelectedValue(null);
      setSelectedLevel(null);
      setAppliedSearch(item.value);
      return;
    }
    setAppliedSearch("");
    setActiveLayer(item.layer);
    setSelectedValue(item.value);
    setSelectedLevel(LEVEL_OF[item.layer]);
  }, [mapConfig]);

  // Switching the polygon layer keeps the active selection and its info visible.
  const onSelectLayer = useCallback((k) => {
    if (!mapConfig[k]) return;
    setActiveLayer((prev) => (prev === k ? null : k));
  }, [mapConfig]);

  const onSelectPolygon = useCallback((val) => {
    setSelectedValue((prev) => {
      if (prev === val) { setSelectedLevel(null); return null; }
      setSelectedLevel(activeLevel);
      return val;
    });
  }, [activeLevel]);

  const loadStatic = async () => {
    try {
      const [f, m] = await Promise.all([fetchFilters(), fetchMeta()]);
      setFilters(f);
      setMeta(m);
      return f;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      const f = await loadStatic();
      if (!cancelled) setLoading(false);
      if (!cancelled && (!f || !f.secciones?.length)) {
        for (let i = 0; i < 5 && !cancelled; i++) {
          await new Promise((r) => setTimeout(r, 2500));
          const r2 = await loadStatic();
          if (r2 && r2.secciones?.length) break;
        }
      }
    };
    boot();
    return () => { cancelled = true; };
  }, []);

  // Load polygon layers + map config once.
  useEffect(() => {
    fetchGeojson().then(setGeojson).catch(() => {});
    fetchCseccion().then(setCseccion).catch(() => {});
    fetchAreasGeojson().then(setAreasGeojson).catch(() => {});
    fetchColoniasGeojson().then(setColoniasGeojson).catch(() => {});
    fetchMapConfig()
      .then((cfg) => {
        setMapConfig(cfg);
        const first = ["secciones", "areas", "colonias"].find((k) => cfg[k]) || null;
        setActiveLayer(first);
      })
      .catch(() => {});
  }, []);

  // All enabled layers load at map start (no polygon selection required).
  // Aggressive clustering keeps the map fast with the full dataset.
  useEffect(() => {
    if (loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const enabled = Object.entries(layers).filter(([, v]) => v).map(([k]) => k);
    if (enabled.length === 0) { setPoints([]); return; }

    debounceRef.current = setTimeout(async () => {
      const params = { layers: enabled.join(",") };
      if (hasSelection) params[effectiveLevel] = selectedValue;
      if (values.unidad) params.unidad = values.unidad;
      if (values.categoria) params.categoria = values.categoria;
      if (appliedSearch) params.search = appliedSearch;
      try {
        const data = await fetchPoints(params);
        setPoints(data);
      } catch (e) { /* ignore */ }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [values, layers, loading, hasSelection, effectiveLevel, selectedValue, appliedSearch]);

  // Summary adapts to the active layer + selected value + committed search.
  useEffect(() => {
    if (loading) return;
    fetchSummary(effectiveLevel, hasSelection ? selectedValue : null, appliedSearch || null)
      .then(setSummary).catch(() => {});
  }, [effectiveLevel, selectedValue, hasSelection, appliedSearch, loading]);

  const onSync = async () => {
    setSyncing(true);
    try {
      await triggerSync();
      await loadStatic();
      await fetchSummary(effectiveLevel, hasSelection ? selectedValue : null, appliedSearch || null).then(setSummary);
      toast.success("Datos actualizados desde Google Sheets");
    } catch (e) {
      toast.error("Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const downloadFile = async (url, filename) => {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error("download failed");
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(u);
  };

  const onDownload = async () => {
    if (!hasSelection) {
      toast.warning(`Selecciona una ${levelLabel.toLowerCase()} en el mapa para descargar el reporte`);
      return;
    }
    setDownloading(true);
    try {
      await downloadFile(reportUrl(effectiveLevel, selectedValue),
        `reporte_${effectiveLevel}_${selectedValue}.pdf`);
      toast.success("Reporte PDF generado");
    } catch (e) {
      toast.error("Error al generar el reporte");
    } finally {
      setDownloading(false);
    }
  };

  const onExportExcel = async () => {
    if (!hasSelection) {
      toast.warning(`Selecciona una ${levelLabel.toLowerCase()} en el mapa para exportar el listado`);
      return;
    }
    setExporting(true);
    try {
      await downloadFile(exportUrl(effectiveLevel, selectedValue),
        `listado_${effectiveLevel}_${selectedValue}.csv`);
      toast.success("Listado exportado a Excel/CSV");
    } catch (e) {
      toast.error("Error al exportar el listado");
    } finally {
      setExporting(false);
    }
  };

  const summaryTitle = appliedSearch
    ? `Resultados para "${appliedSearch}"${hasSelection ? ` · ${levelLabel} ${selectedValue}` : ""}`
    : hasSelection
    ? `Resumen ${LEVEL_ARTICLE[effectiveLevel] || "de la Sección"} ${selectedValue}`
    : "Resumen General (todos los registros)";
  const downloadLabel = hasSelection
    ? `Descargar PDF · ${levelLabel} ${selectedValue}`
    : `Descargar PDF (elige ${levelLabel.toLowerCase()})`;
  const exportLabel = hasSelection ? `Exportar Excel · ${levelLabel} ${selectedValue}` : "Exportar Excel";

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-3">
        <Loader2 className="w-8 h-8 text-guinda animate-spin" />
        <p className="text-sm text-slate-500">Cargando datos territoriales de Tampico...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-guinda flex items-center justify-center shadow-sm">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg sm:text-xl font-bold text-slate-900 leading-tight">
                Plataforma Territorial de Obras y Reportes
              </h1>
              <p className="text-xs text-slate-500">Municipio de Tampico, Tamaulipas · Secciones · Áreas · Colonias</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.is_admin && (
              <Button data-testid="admin-link-btn" onClick={() => navigate("/admin")} variant="outline"
                className="h-10 border-slate-200 text-slate-700 hover:bg-slate-50">
                <ShieldCheck className="w-4 h-4 mr-1.5 text-guinda" /> Administración
              </Button>
            )}
            <Button data-testid="export-excel-btn" onClick={onExportExcel}
              disabled={exporting || !hasSelection} variant="outline"
              className="h-10 border-guinda/30 text-guinda hover:bg-guinda-light disabled:opacity-50">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              {exportLabel}
            </Button>
            <Button data-testid="download-pdf-btn" onClick={onDownload}
              disabled={downloading || !hasSelection}
              className="bg-guinda hover:bg-guinda-dark text-white h-10 disabled:opacity-50">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              {downloadLabel}
            </Button>
            <div className="flex items-center gap-2 pl-2 sm:border-l sm:border-slate-200">
              {user?.picture ? (
                <img src={user.picture} alt="" data-testid="user-avatar" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
              ) : (
                <div data-testid="user-avatar" className="w-8 h-8 rounded-full bg-guinda text-white flex items-center justify-center text-xs font-bold">
                  {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <Button data-testid="logout-btn" onClick={onLogout} variant="ghost"
                className="h-10 text-slate-500 hover:text-red-600 hover:bg-red-50 px-2">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 py-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Sidebar */}
          <aside className="lg:col-span-3 xl:col-span-3">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 lg:sticky lg:top-4 max-h-[calc(100vh-2rem)] overflow-y-auto thin-scroll"
              data-testid="filters-panel">
              <h2 className="font-heading text-sm font-bold text-slate-800 uppercase tracking-wide mb-4">
                Filtros
              </h2>
              <FiltersPanel
                filters={filters} values={values} setValue={setValue}
                layers={layers} toggleLayer={toggleLayer} onReset={onReset}
                onSync={onSync} syncing={syncing} meta={meta} resultCount={points.length}
                activeLayer={activeLayer} activeLevel={activeLevel}
                selectedValue={selectedValue || ""} onSelectValue={(v) => { if (v) { setSelectedValue(v); setSelectedLevel(activeLevel); } else { setSelectedValue(null); setSelectedLevel(null); } }}
                hasSelection={hasSelection}
                searchText={searchText} setSearchText={setSearchText}
                onSearchSubmit={onSearchSubmit} onPickSuggestion={onPickSuggestion}
              />
            </div>
          </aside>

          {/* Map + content */}
          <section className="lg:col-span-9 xl:col-span-9 space-y-5">
            <div className="h-[520px] sm:h-[600px] rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-slate-100 relative">
              <MapView points={points}
                geojson={geojson} areasGeojson={areasGeojson} coloniasGeojson={coloniasGeojson}
                activeLayer={activeLayer} availableLayers={mapConfig} onSelectLayer={onSelectLayer}
                appliedSearch={appliedSearch} selectedLevel={selectedLevel}
                selectedValue={hasSelection ? selectedValue : null} onSelectPolygon={onSelectPolygon} />
              {/* Legend */}
              <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-lg border border-slate-200 shadow-sm px-3 py-2 text-xs space-y-1">
                <div className="font-semibold text-slate-700 mb-0.5">Leyenda</div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#C17A5A" }} /> Actividades / Obras</div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#C03C54" }} /> Reportes Ciudadanos</div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#5A524A" }} /> Activaciones (Loterías)</div>
                {activeLayer === "secciones" && (
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm border border-guinda" style={{ background: "rgba(192,60,84,0.25)" }} /> Secciones (INE)</div>
                )}
                {activeLayer === "areas" && (
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ border: "1px solid #A87A1E", background: "rgba(201,154,46,0.3)" }} /> Áreas</div>
                )}
                {activeLayer === "colonias" && (
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ border: "1px solid #556B14", background: "rgba(107,142,35,0.3)" }} /> Colonias</div>
                )}
              </div>
            </div>

            {activeLayer === "secciones" && (
              <SeccionInfoCards data={cseccion} selectedValue={selectedLevel === "seccion" ? selectedValue : ""} />
            )}

            <div>
              <h2 className="font-heading text-base font-bold text-slate-800 mb-3" data-testid="summary-title">
                {summaryTitle}
              </h2>
              <KpiCards summary={summary} />
            </div>

            <ClassificationChart summary={summary} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <EncuestaCard summary={summary}
                seccion={effectiveLevel === "seccion" && hasSelection ? selectedValue : ""} />
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h3 className="font-heading text-base font-bold text-slate-900 mb-3">Unidades Responsables</h3>
                {summary?.unidades?.length ? (
                  <div className="flex flex-wrap gap-2" data-testid="unidades-list">
                    {summary.unidades.map((u) => (
                      <span key={u} className="text-xs bg-slate-100 text-slate-700 rounded-full px-3 py-1 border border-slate-200">
                        {u}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Sin datos.</p>
                )}
              </div>
            </div>
          </section>
        </div>

        <footer className="mt-8 pb-4 text-center text-xs text-slate-400">
          Datos sincronizados en vivo desde Google Sheets · Secciones (INE) · Áreas · Colonias
        </footer>
      </main>
    </div>
  );
}
